
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import { PDFDocument } from 'pdf-lib';
import { Transaction, MonthlySummary } from '../types';

import { supabase } from '../lib/supabase';

interface GroupSettlementRow {
  from: string;
  to: string;
  amount: number;
}

interface GroupMonthlyBreakdown {
  monthKey: string | null;
  total: number;
  contributions: Record<string, number>;
  settlements: GroupSettlementRow[];
  share: number;
  participatingCount: number;
  currentMonthExclusions: string[];
  allPayerIds: string[];
}

export const saveSecurePDF = async (doc: jsPDF, filename: string, password?: string, extraParams: any = {}) => {
  if (!password) {
    doc.save(filename);
    return;
  }

  try {
    // We call the Edge Function because client-side JS libraries for PDF encryption are notoriously unstable in browsers.
    // The server-side function uses a specialized build of jsPDF that supports 128-bit RC4 encryption perfectly.
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Unauthorized");

    const response = await supabase.functions.invoke('dispatch-reports', {
      body: { 
        ...extraParams,
        downloadOnly: true,
        targetUserId: session.user.id
      }
    });

    if (response.error) throw response.error;
    if (response.data?.pdf) {
      const link = document.createElement('a');
      link.href = response.data.pdf;
      link.download = response.data.filename || filename;
      link.click();
    }
  } catch (error) {
    console.error('Vault encryption failed, falling back to standard save:', error);
    doc.save(filename);
  }
};

export const generateProfessionalPDF = (
  transactions: Transaction[], 
  summary: MonthlySummary, 
  monthLabel: string,
  userEmail: string
) => {
  const doc = new jsPDF();
  const primaryColor = [15, 23, 42]; // Slate 900
  const accentColor = [251, 191, 36]; // Amber 400

  // Header
  doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.rect(0, 0, 210, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('EXPENSE STATEMENT', 20, 25);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`REPORT MONTH: ${monthLabel.toUpperCase()}`, 140, 20);
  doc.text(`ACCOUNT: ${userEmail}`, 140, 26);

  // Summary Tiles
  let y = 55;
  doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.setFontSize(12);
  doc.text('MONEY SUMMARY', 20, y);
  
  const summaryRows = [
    ['Total Income', `INR ${summary.income.toLocaleString()}`],
    ['Total Expenses', `INR ${summary.expense.toLocaleString()}`],
    ['Outstanding Debt', `INR ${summary.debt.toLocaleString()}`],
    ['Net Cash Flow', `INR ${(summary.income - summary.expense).toLocaleString()}`]
  ];

  (doc as any).autoTable({
    startY: y + 5,
    head: [['Metric', 'Value']],
    body: summaryRows,
    theme: 'grid',
    headStyles: { fillColor: primaryColor, textColor: accentColor },
    styles: { fontStyle: 'bold' }
  });

  // Transaction History
  y = (doc as any).lastAutoTable.finalY + 15;
  doc.text('TRANSACTION LIST', 20, y);

  const tableData = transactions.map(t => [
    format(new Date(t.date), 'dd MMM yyyy'),
    t.category,
    t.type.toUpperCase(),
    `INR ${Number(t.amount).toLocaleString()}`,
    t.note || '-'
  ]);

  (doc as any).autoTable({
    startY: y + 5,
    head: [['Date', 'Category', 'Type', 'Amount', 'Note']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: primaryColor, textColor: 255 },
    columnStyles: {
      3: { fontStyle: 'bold', halign: 'right' }
    }
  });

  // Footer / Legal
  const pageCount = (doc as any).internal.getNumberOfPages();
  for(let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Confidential Financial Document | Generated on ${format(new Date(), 'dd/MM/yyyy HH:mm')} | Page ${i} of ${pageCount}`, 105, 285, { align: 'center' });
  }

  return doc;
};

export const generateGroupSettlementPDF = (
  groupName: string,
  totalSpent: number,
  memberCount: number,
  contributions: Record<string, number>,
  userMap: Record<string, string>,
  settlements: GroupSettlementRow[],
  transactions: Transaction[],
  selectedMonth?: string,
  isAllTime?: boolean,
  monthlyBreakdowns: GroupMonthlyBreakdown[] = []
) => {
  const doc = new jsPDF();
  const primaryColor = [15, 23, 42];

  doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.rect(0, 0, 210, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.text('GROUP TREASURY REPORT', 20, 25);
  doc.setFontSize(10);
  doc.text(`GROUP: ${groupName.toUpperCase()}`, 140, 20);
  
  if (isAllTime) {
    doc.text('PERIOD: ALL TIME SUMMARY', 140, 26);
  } else if (selectedMonth) {
    const monthLabel = format(new Date(selectedMonth + '-01'), 'MMMM yyyy');
    doc.text(`PERIOD: ${monthLabel.toUpperCase()}`, 140, 26);
  }

  let y = 55;

  if (isAllTime && monthlyBreakdowns && monthlyBreakdowns.length > 0) {
    // MULTI-MONTH BREAKDOWN
    doc.setTextColor(0);
    doc.setFontSize(14);
    doc.text('MONTHLY SETTLEMENT DISPATCH', 20, y);
    y += 10;

    monthlyBreakdowns.forEach((breakdown) => {
      const monthDisplay = format(new Date(breakdown.monthKey + '-01'), 'MMMM yyyy').toUpperCase();
      
      if (y > 240) {
        doc.addPage();
        y = 25;
      }

      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.setFont("helvetica", "bold");
      doc.text(`${monthDisplay} | TOTAL: INR ${breakdown.total.toLocaleString()}`, 20, y);
      y += 3;

      (doc as any).autoTable({
        startY: y + 2,
        head: [['Month Total', 'Fair Share', 'People In Split']],
        body: [[
          `INR ${breakdown.total.toLocaleString()}`,
          `INR ${Math.round(breakdown.share).toLocaleString()}`,
          `${breakdown.participatingCount}`
        ]],
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: 255 },
        styles: { halign: 'center' },
        didDrawPage: (data: any) => {
          y = data.cursor.y + 8;
        }
      });

      const contributionRows = breakdown.allPayerIds.map((payerId) => {
        const paid = breakdown.contributions[payerId] || 0;
        const isExcluded = breakdown.currentMonthExclusions.includes(payerId);
        const balance = isExcluded ? 0 : paid - breakdown.share;

        return [
          userMap[payerId] || 'Unassigned Member',
          `INR ${paid.toLocaleString()}`,
          isExcluded ? '-' : `INR ${Math.round(breakdown.share).toLocaleString()}`,
          isExcluded ? 'Not in split' : `${balance >= 0 ? '+' : '-'}INR ${Math.abs(balance).toLocaleString()}`
        ];
      });

      (doc as any).autoTable({
        startY: y,
        head: [['Person', 'Paid', 'Share', 'Net Balance']],
        body: contributionRows,
        theme: 'striped',
        headStyles: { fillColor: [241, 245, 249], textColor: primaryColor },
        columnStyles: {
          1: { halign: 'right' },
          2: { halign: 'right' },
          3: { halign: 'right' }
        },
        didDrawPage: (data: any) => {
          y = data.cursor.y + 8;
        }
      });

      const settlementRows = (breakdown.settlements || []).map((s) => [s.from, 'PAYS', s.to, `INR ${s.amount.toLocaleString()}`]);
      
      (doc as any).autoTable({
        startY: y,
        head: [['From', 'Action', 'To', 'Amount']],
        body: settlementRows.length > 0 ? settlementRows : [['-', 'No pending payments', '-', '-']],
        theme: 'grid',
        headStyles: { fillColor: [251, 191, 36], textColor: primaryColor },
        didDrawPage: (data: any) => {
          y = data.cursor.y + 15;
        }
      });
    });
  } else {
    // SINGLE MONTH OR AGGREGATED
    doc.setTextColor(0);
    doc.setFontSize(14);
    doc.text('SETTLEMENT ACTION PLAN', 20, y);

    const contributionRows = Object.keys(contributions).map((payerId) => {
      const paid = contributions[payerId] || 0;
      const fairShare = memberCount > 0 ? totalSpent / memberCount : 0;
      const balance = paid - fairShare;

      return [
        userMap[payerId] || 'Unassigned Member',
        `INR ${paid.toLocaleString()}`,
        `INR ${Math.round(fairShare).toLocaleString()}`,
        `${balance >= 0 ? '+' : '-'}INR ${Math.abs(balance).toLocaleString()}`
      ];
    });

    (doc as any).autoTable({
      startY: y + 5,
      head: [['Person', 'Paid', 'Share', 'Net Balance']],
      body: contributionRows,
      theme: 'striped',
      headStyles: { fillColor: [241, 245, 249], textColor: primaryColor },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' }
      }
    });
    
    const settlementRows = settlements.map(s => [s.from, 'PAYS', s.to, `INR ${s.amount.toLocaleString()}`]);
    (doc as any).autoTable({
      startY: (doc as any).lastAutoTable.finalY + 10,
      head: [['From', 'Action', 'To', 'Amount']],
      body: settlementRows.length > 0 ? settlementRows : [['-', 'No pending payments', '-', '-']],
      theme: 'grid',
      headStyles: { fillColor: [251, 191, 36], textColor: primaryColor }
    });
  }

  return doc;
};
